import { Button } from "@itk/mui-components";
// eslint-disable-next-line @itk/prefer-itk-imports
import { ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { DekoxIcon } from "src/components";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { configuratorActions } from "src/pages/configurator/configurator-store";
import { addEditLabelActions } from "../add-edit-label/add-edit-label-store";
import { applicationActions } from "src/components/application-store";
import { useNavigate, useParams } from "react-router";
import { ROUTES_URL } from "src/utils/constants";
import { TConfigureProductSearchParams } from "src/pages/configure-product/configure-product-types";

const mapStateToProps = (state: RootState) => ({
  selectedProducts: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.selectedProducts,
  configurationsState: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.configurationsState
});

const ownActions = {
  setSelectedProducts: configuratorActions.setSelectedProducts,
  deleteProjectLineItemsRequest: configuratorActions.deleteProjectLineItemsRequest,
  getConfigurationsRequest: configuratorActions.getConfigurationsRequest,
  setIsShowingAddEditLabel: addEditLabelActions.setIsShowingAddEditLabel,
  setWarningAction: applicationActions.setWarningAction
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const SelectedProductsBase = (props: Props) => {
  const {
    selectedProducts,
    configurationsState,
    setSelectedProducts,
    setIsShowingAddEditLabel,
    setWarningAction,
    deleteProjectLineItemsRequest,
    getConfigurationsRequest
  } = props;

  const theme = useTheme();
  const navigate = useNavigate();
  const { projectId = "" } = useParams<{ projectId: string }>();

  const genericTranslationsT = useGenericTranslationsT();
  const configuratorPageT = useConfiguratorPageT();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // A line item carries no configuration id of its own, so the single-selection Edit has to look up
  // which configuration owns it before it can build the configure-product route.
  const configurationIdBySelectedProduct = useMemo(() => {
    if (selectedProducts.length !== 1) return "";

    const owningConfiguration = (configurationsState?.configurations ?? []).find(configuration =>
      (configuration.systemProductConfigurationItems ?? []).some(item => item.systemProductConfigurationItemId === selectedProducts[0])
    );

    return owningConfiguration?.systemProductConfigurationId ?? "";
  }, [configurationsState, selectedProducts]);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // One product opens the full configure-product view for it, which offers the hierarchy and parts list
  // the bulk editor cannot. Two or more go to the bulk editor, grouped by product type.
  const handleEdit = () => {
    handleMenuClose();

    if (selectedProducts.length === 1) {
      if (!configurationIdBySelectedProduct) return;

      navigate({
        pathname: ROUTES_URL.CONFIGURE_PRODUCT.build(projectId, configurationIdBySelectedProduct),
        search: `?${new URLSearchParams({ projectLineItemId: selectedProducts[0] } as TConfigureProductSearchParams).toString()}`
      });
      return;
    }

    navigate(ROUTES_URL.BULK_CONFIGURE_PRODUCTS.build(projectId));
  };

  const handleAddLabel = () => {
    handleMenuClose();
    setIsShowingAddEditLabel(true);
  };

  const handleDelete = () => {
    handleMenuClose();
    setWarningAction({
      confirm: () => {
        deleteProjectLineItemsRequest({ systemProductConfigurationItemIds: selectedProducts }, isSuccess => {
          if (isSuccess) {
            getConfigurationsRequest(projectId);
            setSelectedProducts([]);
          }
        });
        setWarningAction(undefined);
      },
      dialogProps: {
        message: configuratorPageT.areYouSureYouWantToDeleteSelectedProducts,
        confirmButtonTranslation: {
          t: genericTranslationsT,
          k: genericTranslationsK,
          field: "delete"
        }
      },
      variant: "confirmCancel"
    });
  };

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" px={3} py={1.5} bgcolor={theme.palette.customColors.white} borderTop={`1px solid ${theme.palette.customColors.grey}`}>
      <Typography>{genericTranslationsT.selected(selectedProducts.length.toString())}</Typography>
      <Stack direction="row" alignContent="center" gap={2}>
        <Button
          t={genericTranslationsT}
          k={genericTranslationsK}
          field="cancel"
          variant="outlined"
          onClick={() => {
            setSelectedProducts([]);
          }}
        />
        <Button
          t={genericTranslationsT}
          k={genericTranslationsK}
          field="actions"
          variant="contained"
          onClick={e => {
            setAnchorEl(e.currentTarget);
          }}
        />
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleMenuClose}
        elevation={2}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              minWidth: 180,
              boxShadow: "0px 4px 20px rgba(0,0,0,0.12)",
              transform: "translateY(-10px) !important"
            }
          }
        }}
      >
        <MenuItem onClick={handleAddLabel} sx={{ gap: 0.4, py: 1.4, borderBottom: `1px solid ${theme.palette.customColors.grey}` }}>
          <ListItemIcon>
            <DekoxIcon icon="add" fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Typography>{configuratorPageT.addLabel}</Typography>
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEdit} sx={{ gap: 0.4, py: 1.4, borderBottom: `1px solid ${theme.palette.customColors.grey}` }}>
          <ListItemIcon>
            <DekoxIcon icon="edit" fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Typography>{genericTranslationsT.edit}</Typography>
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ gap: 0.4, py: 1.4 }}>
          <ListItemIcon>
            <DekoxIcon icon="delete" color="error" fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Typography color="error">{genericTranslationsT.delete}</Typography>
          </ListItemText>
        </MenuItem>
      </Menu>
    </Stack>
  );
};

export const SelectedProducts = connect(mapStateToProps, ownActions)(SelectedProductsBase);
